import { BoardingStateEnum } from "../enums";

/**
 * Deep Ride Investigation — client contract, mirroring the backend RideInvestigationDto
 * (GET /api/v1/core/c/{companyId}/rides/{rideId}/investigation).
 *
 * The raw GPS trail is NOT part of this payload; it continues to load via the tracking-info endpoint
 * and the ITrackingRenderState machinery. This adds the semantic layer keyed by timestamp: summary,
 * stations (scheduled vs actual), a typed event timeline, a passenger-count step function and anomalies.
 */

export enum RideInvestigationEventTypeEnum {
  RideStarted = "RideStarted",
  ArrivedAtStation = "ArrivedAtStation",
  StoppedAtStation = "StoppedAtStation",
  DepartedStation = "DepartedStation",
  PassengerBoarded = "PassengerBoarded",
  PassengerNoShow = "PassengerNoShow",
  DetourEntered = "DetourEntered",
  DetourExited = "DetourExited",
  GpsGap = "GpsGap",
  UnexpectedStop = "UnexpectedStop",
  Incident = "Incident",
  RideEnded = "RideEnded",
}

export enum RideInvestigationSeverityEnum {
  Info = "Info",
  Warning = "Warning",
  Critical = "Critical",
}

export enum RideInvestigationAnomalyTypeEnum {
  MissingGps = "MissingGps",
  GpsGap = "GpsGap",
  Detour = "Detour",
  UnexpectedStop = "UnexpectedStop",
  LateArrival = "LateArrival",
  NoShow = "NoShow",
  MissingArrival = "MissingArrival",
}

export type TRideInvestigationMeta = Record<string, number | string | boolean>;

export interface IRideInvestigationSummaryDto {
  rideId: number;
  status: string;
  direction?: string;
  title?: string;
  courseId?: number | null;
  driverName?: string;
  driverPhone?: string;
  vehiclePlate?: string;
  supplierName?: string;
  scheduledStart?: TDateISO;
  scheduledEnd?: TDateISO;
  actualStart?: TDateISO;
  actualEnd?: TDateISO;
  startStatus?: string;
  plannedDistanceKm?: number;
  actualDistanceKm?: number;
  distanceGapKm?: number;
  plannedDurationSec?: number;
  actualDurationSec?: number;
  avgSpeedKmh?: number;
  hadDetour?: boolean;
  hasGps?: boolean;
  passengersCount?: number;
  seatsCount?: number;
  seatsCountByDriver?: number;
  stationsCount: number;
  boardedCount: number;
  noShowCount: number;
  delayStatus?: string;
  delayMinutes?: number;
}

export interface IRideInvestigationRouteDto {
  plannedPolyline?: string;
  plannedDistanceKm?: number;
  plannedDurationSec?: number;
}

export interface IRideInvestigationStationDto {
  id: number;
  sequence: number;
  name: string;
  latitude?: number;
  longitude?: number;
  areaId?: number;
  scheduledArrival?: TDateISO;
  actualArrival?: TDateISO;
  stopStart?: TDateISO;
  stopEnd?: TDateISO;
  dwellSeconds?: number;
  delayMinutes?: number;
  isBoarding?: boolean;
  isDropoff?: boolean;
  boardingCount: number;
  alightingCount: number;
  seatsCountByDriver?: number;
  occupancyAfter: number;
}

/** One booked passenger: who they are, where they got on and off, and what the driver recorded. */
export interface IRideInvestigationPassengerDto {
  id: number;
  name?: string;
  phone?: string;
  unitName?: string;
  code?: string;
  seats: number;
  bookingId?: number;
  pickupStationId?: number;
  pickupStationName?: string;
  /** 0-based position of the pickup station along the route. */
  pickupSequence?: number;
  pickupScheduledArrival?: TDateISO;
  pickupActualArrival?: TDateISO;
  dropoffStationId?: number;
  dropoffStationName?: string;
  dropoffSequence?: number;
  dropoffScheduledArrival?: TDateISO;
  dropoffActualArrival?: TDateISO;
  boardedStatus?: BoardingStateEnum;
  boardedAt?: TDateISO;
  boardedNote?: string;
  hasDog?: boolean;
  catchARide?: boolean;
  /** Moment the panel seeks playback to for this passenger (boarding stamp, else the pickup arrival). */
  timestamp?: TDateISO;
}

export interface IRideInvestigationEventDto {
  id: string;
  type: RideInvestigationEventTypeEnum;
  severity: RideInvestigationSeverityEnum;
  timestamp: TDateISO;
  endTimestamp?: TDateISO;
  stationId?: number;
  stationName?: string;
  passengerId?: number;
  passengerName?: string;
  labelKey: string;
  detail?: string;
  latitude?: number;
  longitude?: number;
  meta?: TRideInvestigationMeta;
}

export interface IRideInvestigationOccupancyPointDto {
  t: TDateISO;
  count: number;
  stationId?: number;
}

export interface IRideInvestigationAnomalyDto {
  type: RideInvestigationAnomalyTypeEnum;
  severity: RideInvestigationSeverityEnum;
  timestamp?: TDateISO;
  endTimestamp?: TDateISO;
  labelKey: string;
  stationId?: number;
  meta?: TRideInvestigationMeta;
}

export interface IRideInvestigationPlaybackDto {
  startTime?: TDateISO;
  endTime?: TDateISO;
  hasGps: boolean;
  trackingPointCount: number;
  firstTrackingAt?: TDateISO;
  lastTrackingAt?: TDateISO;
}

export interface IRideInvestigationDto {
  summary: IRideInvestigationSummaryDto;
  route: IRideInvestigationRouteDto;
  stations: IRideInvestigationStationDto[];
  passengers: IRideInvestigationPassengerDto[];
  events: IRideInvestigationEventDto[];
  occupancyTimeline: IRideInvestigationOccupancyPointDto[];
  anomalies: IRideInvestigationAnomalyDto[];
  playback: IRideInvestigationPlaybackDto;
}
